/*!
 * melody-harmonizer.js — chords that FIT a melody, for a melody that has none.
 *
 * Browser global `window.MelodyHarmonizer` + CommonJS export; no DOM.
 * Guard: tests/melody-harmonizer.mjs.
 *
 * ── WHY THIS EXISTS NEXT TO chord-finder/engine.js ───────────────────────
 * That engine answers "which chords ARE SOUNDING in this recording". It reads
 * harmony off a chromagram, and it is right to refuse when there is none: a
 * lone voice states one pitch at a time, so there is no chord in the audio to
 * find. Support #16.09.2026 («Голос не понимант», /chord-finder) is exactly
 * that case, and the measured behaviour was worse than a refusal — a sung
 * melody came back as 21 «chords» (E5, F5, G5, D5), one per melody note, most
 * of them drawn with the solid border that means "confident".
 *
 * This file answers the OTHER question, the one the singer was actually
 * asking: "which chords would go UNDER what I just sang". That is not
 * transcription, it is harmonisation, and it is a different problem with a
 * different kind of answer — there is no single correct one, only better and
 * worse fits. So nothing here is presented as a reading of the recording.
 *
 * ── WHAT IS REUSED, AND WHY THAT MATTERS ─────────────────────────────────
 * The KEY is not decided here. A duration-weighted chroma is built from the
 * melody and handed to `window.__chromaStream.key()` — the same Krumhansl /
 * Temperley profiles key-detector.js uses for audio, the ones written after
 * comment #886 when a hand-rolled chroma measured 20% against that port's
 * 100%. A second "good enough" key finder in this file would quietly be the
 * 20% one again. `opts.keyFn` exists so the node guard can inject that same
 * function without a browser, NOT so callers can bring their own theory.
 *
 * The OUTPUT shape is deliberately chord-finder's segment shape
 * ({from,to,root,quality,sym,confidence,margin}), so `ChordEngine.nameOf`,
 * `.transpose`, `.simplify`, the fingering generator, the diagram renderer,
 * the tab view and every exporter work on it unchanged.
 *
 * ── THE METHOD, AND WHY IT IS DELIBERATELY NOT LEARNED ───────────────────
 * Harmonising a melody is a solved music-theory exercise, and the three rules
 * that solve it are the ones a teacher gives:
 *   1. a chord fits a bar when the bar's LONG and ACCENTED notes are its chord
 *      tones (passing notes are short and off the beat, and are supposed to
 *      clash);
 *   2. chords move by descending fifth more than by anything else — that
 *      motion is the engine of functional harmony, and it is what makes
 *      ii-V-I sound like an argument rather than a list;
 *   3. phrases start and end at home.
 * Each is one term below, each is readable, and each can be measured. A model
 * would need a corpus we do not have, would cost megabytes on the 4 GB phone
 * this started on, and could not explain itself to a beginner — who is the
 * entire audience for "what do I play under this".
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.MelodyHarmonizer = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ── scales, degrees, candidate chords ──────────────────────────────────
   *
   * The candidate set is SMALL on purpose. Offering every chord that could be
   * justified produces charts nobody plays: the beginner who hummed a tune
   * wants six shapes, not a jazz reharmonisation. Everything here is either
   * diatonic or one of the three borrowed chords that turn up constantly in
   * popular music (bVII, iv, V7).
   *
   * `prior` is a small bonus, in the same units as the fit score, that breaks
   * ties towards the plainer chord. Without it a passing 7th in the melody is
   * enough to prefer V7 over V in every single bar.
   */
  var MAJOR_SCALE = [0, 2, 4, 5, 7, 9, 11];
  var MINOR_SCALE = [0, 2, 3, 5, 7, 8, 10];

  var MAJOR_DEGREES = [
    { pc: 0,  quality: 'maj',  degree: 'I',    prior: 0.055 },
    { pc: 2,  quality: 'min',  degree: 'ii',   prior: 0.030 },
    { pc: 4,  quality: 'min',  degree: 'iii',  prior: 0.018 },
    { pc: 5,  quality: 'maj',  degree: 'IV',   prior: 0.045 },
    { pc: 7,  quality: 'maj',  degree: 'V',    prior: 0.045 },
    { pc: 9,  quality: 'min',  degree: 'vi',   prior: 0.038 },
    { pc: 11, quality: 'dim',  degree: 'vii°', prior: 0.000 },
    { pc: 7,  quality: '7',    degree: 'V7',   prior: 0.012 },
    { pc: 10, quality: 'maj',  degree: '♭VII', prior: 0.006 },
    { pc: 5,  quality: 'min',  degree: 'iv',   prior: 0.004 }
  ];

  var MINOR_DEGREES = [
    { pc: 0,  quality: 'min',  degree: 'i',    prior: 0.055 },
    { pc: 2,  quality: 'dim',  degree: 'ii°',  prior: 0.000 },
    { pc: 3,  quality: 'maj',  degree: '♭III', prior: 0.038 },
    { pc: 5,  quality: 'min',  degree: 'iv',   prior: 0.045 },
    { pc: 7,  quality: 'min',  degree: 'v',    prior: 0.024 },
    { pc: 7,  quality: 'maj',  degree: 'V',    prior: 0.030 },
    { pc: 8,  quality: 'maj',  degree: '♭VI',  prior: 0.038 },
    { pc: 10, quality: 'maj',  degree: '♭VII', prior: 0.040 },
    { pc: 7,  quality: '7',    degree: 'V7',   prior: 0.010 },
    { pc: 2,  quality: 'min',  degree: 'ii',   prior: 0.004 }
  ];

  /* The intervals each quality stands for. Kept in step with
   * ChordEngine.QUALITIES by the guard, which compares the two tables rather
   * than trusting this comment. */
  var IVALS = {
    maj: [0, 4, 7],
    min: [0, 3, 7],
    '7': [0, 4, 7, 10],
    min7: [0, 3, 7, 10],
    maj7: [0, 4, 7, 11],
    sus4: [0, 5, 7],
    dim: [0, 3, 6],
    aug: [0, 4, 8],
    '5': [0, 7]
  };
  var SYMS = {
    maj: '', min: 'm', '7': '7', min7: 'm7', maj7: 'maj7',
    sus4: 'sus4', dim: 'dim', aug: 'aug', '5': '5'
  };

  /* How much a melody note counts as evidence FOR a chord, by its interval
   * above the chord root. Root and third carry the identity, the fifth is
   * nearly free (it is shared by the relative minor), the seventh is real but
   * weaker evidence. */
  var TONE_WEIGHT = [1.0, 0.95, 0.8, 0.6];
  /* A note outside the chord but inside the key: a colour, not a clash. */
  var IN_KEY_TONE = 0.28;
  /* The perfect fourth over a MAJOR third is the one diatonic note that does
   * clash — it is the "avoid note" every arranging book names. */
  var AVOID_TONE = 0.05;
  /* A flat ninth is harsh against anything. */
  var FLAT_NINE = -0.2;
  /* Outside the key entirely. Negative, so a bar full of them cannot be
   * explained by any diatonic chord and the weak-evidence gate below fires. */
  var OUT_OF_KEY = -0.35;

  /** Fit of one pitch class against one candidate, in [-0.35, 1]. */
  function toneFit(pc, cand, scalePcs) {
    var iv = ((pc - cand.pc) % 12 + 12) % 12;
    var ivals = IVALS[cand.quality];
    for (var i = 0; i < ivals.length; i++) {
      if (ivals[i] === iv) return TONE_WEIGHT[i] !== undefined ? TONE_WEIGHT[i] : 0.5;
    }
    if (iv === 1) return FLAT_NINE;
    if (iv === 5 && ivals.indexOf(4) >= 0) return AVOID_TONE;
    return scalePcs.indexOf(pc) >= 0 ? IN_KEY_TONE : OUT_OF_KEY;
  }

  /* ── melody in, notes out ───────────────────────────────────────────────
   *
   * Accepts the frame shape PitchTracker emits ({timeS,freqHz,confidence}) and
   * the one a live detector emits ({t,hz,clarity}), because both exist in this
   * codebase and a caller should not have to translate.
   *
   * ⚠️ Rounding each frame to the nearest semitone and cutting where the
   * number changes does NOT work on a real voice: a sung note wanders ±40
   * cents and every wobble across a semitone boundary becomes a note. The
   * median filter below is what makes the boundary stable, and `minNoteSec`
   * throws away what survives it anyway.
   */
  function pick(o, names) {
    for (var i = 0; i < names.length; i++) {
      if (o[names[i]] !== undefined && o[names[i]] !== null) return o[names[i]];
    }
    return undefined;
  }

  function hzToMidi(hz) { return 69 + 12 * Math.log(hz / 440) / Math.LN2; }

  function median(a) {
    if (!a.length) return 0;
    var s = a.slice().sort(function (x, y) { return x - y; });
    var m = s.length >> 1;
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
  }

  function notesFromPitch(frames, opts) {
    opts = opts || {};
    var minConf = opts.minConf === undefined ? 0.6 : opts.minConf;
    var minNoteSec = opts.minNoteSec === undefined ? 0.09 : opts.minNoteSec;
    var medianWin = opts.medianWin === undefined ? 5 : opts.medianWin;
    var maxGapSec = opts.maxGapSec === undefined ? 0.08 : opts.maxGapSec;

    var pts = [];
    for (var i = 0; i < frames.length; i++) {
      var f = frames[i];
      var t = pick(f, ['timeS', 't', 'time']);
      var hz = pick(f, ['freqHz', 'hz', 'freq']);
      var c = pick(f, ['confidence', 'clarity', 'conf']);
      if (t === undefined || !(hz > 0)) continue;
      if (c !== undefined && c < minConf) continue;
      pts.push({ t: t, midi: hzToMidi(hz) });
    }
    if (pts.length < 2) return [];

    // Median-smooth the MIDI track, then quantise. Smoothing first and
    // rounding second is the whole point: rounding first destroys the
    // information the median needs.
    var sm = [];
    var half = Math.max(1, medianWin >> 1);
    for (i = 0; i < pts.length; i++) {
      var w = [];
      for (var k = Math.max(0, i - half); k <= Math.min(pts.length - 1, i + half); k++) w.push(pts[k].midi);
      sm.push({ t: pts[i].t, midi: median(w) });
    }

    var hop = median(sm.slice(1).map(function (p, idx) { return p.t - sm[idx].t; })) || 0.01;
    var out = [];
    var cur = null;
    for (i = 0; i < sm.length; i++) {
      var n = Math.round(sm[i].midi);
      var brokeGap = cur && (sm[i].t - cur.last) > maxGapSec;
      if (cur && cur.midi === n && !brokeGap) {
        cur.last = sm[i].t;
        cur.cents.push(sm[i].midi);
        continue;
      }
      if (cur) out.push(cur);
      cur = { midi: n, from: sm[i].t, last: sm[i].t, cents: [sm[i].midi] };
    }
    if (cur) out.push(cur);

    var notes = [];
    for (i = 0; i < out.length; i++) {
      var d = out[i].last - out[i].from + hop;
      if (d < minNoteSec) continue;
      notes.push({ from: out[i].from, to: out[i].from + d, midi: out[i].midi });
    }
    return notes;
  }

  /* ── the key ────────────────────────────────────────────────────────────
   *
   * A chroma weighted by how LONG each pitch class is held, not by how often
   * it appears: a tonic held for a bar says more about the key than four
   * passing quavers, and an unweighted histogram gets that backwards.
   */
  function chromaFromNotes(notes) {
    var ch = new Float64Array(12);
    for (var i = 0; i < notes.length; i++) {
      var d = Math.max(0, notes[i].to - notes[i].from);
      ch[((notes[i].midi % 12) + 12) % 12] += d;
    }
    return ch;
  }

  function keyOf(notes, opts) {
    opts = opts || {};
    if (opts.key !== undefined && opts.key !== null && opts.mode) {
      return { tonic: opts.key, mode: opts.mode, confidence: opts.keyConfidence || null, given: true };
    }
    var fn = opts.keyFn ||
      (typeof window !== 'undefined' && window.__chromaStream && window.__chromaStream.key);
    if (!fn) return null;
    var r = fn(chromaFromNotes(notes));
    if (!r) return null;
    return { tonic: r.tonic, mode: r.mode, confidence: r.confidence, given: false };
  }

  /* ── harmonic windows ───────────────────────────────────────────────────
   *
   * One chord per window, windows of equal length. Two beats is the default
   * because popular song changes chord about that often; at one beat the chart
   * becomes a chord per note again, which is the defect this file exists to
   * avoid.
   *
   * ⚠️ The grid starts at the FIRST NOTE, not at zero. A singer who took a
   * breath before starting would otherwise have every bar line shifted by that
   * breath, and every downbeat accent land on the wrong note.
   */
  function windowsFor(notes, opts) {
    var bpm = opts.bpm;
    var winSec = opts.windowSec;
    if (!winSec) {
      var beat = (bpm >= 40 && bpm <= 240) ? 60 / bpm : null;
      winSec = beat ? beat * (opts.beatsPerChord || 2) : 1.6;
    }
    winSec = Math.max(0.6, Math.min(4, winSec));
    var t0 = notes[0].from;
    var end = notes[notes.length - 1].to;
    var wins = [];
    for (var t = t0; t < end - 1e-6; t += winSec) {
      wins.push({ from: t, to: Math.min(end, t + winSec) });
    }
    return wins;
  }

  /* Evidence for every candidate in one window.
   *
   * `weight` is duration inside the window times a downbeat accent. Both
   * matter and they matter for different reasons: duration is why a held note
   * outvotes a passing one, accent is why the note ON the beat outvotes an
   * equally long note that merely spills over from the bar before. */
  function scoreWindow(win, notes, cands, scalePcs) {
    var acc = new Float64Array(cands.length);
    var total = 0;
    var span = win.to - win.from;
    for (var i = 0; i < notes.length; i++) {
      var n = notes[i];
      var ov = Math.min(n.to, win.to) - Math.max(n.from, win.from);
      if (ov <= 0) continue;
      var onBeat = (n.from >= win.from - 1e-9 && n.from < win.from + span * 0.2);
      var w = ov * (onBeat ? 1.6 : 1);
      total += w;
      var pc = ((n.midi % 12) + 12) % 12;
      for (var c = 0; c < cands.length; c++) acc[c] += w * toneFit(pc, cands[c], scalePcs);
    }
    if (total <= 0) return null;
    for (var c2 = 0; c2 < cands.length; c2++) acc[c2] = acc[c2] / total + cands[c2].prior;
    return acc;
  }

  /* ── chord-to-chord motion ──────────────────────────────────────────────
   *
   * ⚠️⚠️ THE FIRST VERSION OF THIS FUNCTION HAD THE SIGN BACKWARDS: it charged
   * 0.02 for holding a chord and paid up to 0.075 for moving. That is an
   * engine for restlessness, and it showed — Ode to Joy came back as sixteen
   * different chords in sixteen seconds, a new one every window, which is the
   * same unreadable confetti chord-finder's own Viterbi exists to prevent.
   *
   * CHANGING costs. A change has to be bought either by the melody (the new
   * chord fits the bar much better) or by function (the root moves down a
   * fifth, which is the motion that chains vi-ii-V-I together and the reason
   * functional harmony sounds like an argument rather than a list). Holding is
   * free, because real songs hold one chord for bars at a time.
   *
   * The cost is CALIBRATED, not chosen. Measured on four progressions read at
   * bar-length and at half-bar windows, plus the harmonic rhythm of a tune
   * whose real rhythm is one chord per bar (segments per window; 0.5 is a
   * chord every other window, which is what a song does):
   *
   *   cost   bars@2s   bars@1s   rhythm
   *   0      16/16      8/16     0.88   ← a new chord almost every window
   *   0.05   16/16     11/16     0.81
   *   0.12   16/16     14/16     0.56
   *   0.16   16/16     15/16     0.56   ← here
   *   0.22   15/16     16/16     0.56   ← starts smearing bar-aligned changes
   *
   * Re-run: tests/melody-harmonizer.mjs (and the sweep it grew out of).
   */
  var CHANGE_COST = 0.16;

  function motionBonus(prev, next, cost) {
    if (prev.pc === next.pc && prev.quality === next.quality) return 0;
    var iv = ((next.pc - prev.pc) % 12 + 12) % 12;
    var b = -(cost === undefined ? CHANGE_COST : cost);
    if (iv === 5) b += 0.075;                 // down a fifth: V→I, ii→V, vi→ii
    else if (iv === 2) b += 0.04;             // up a second: IV→V
    else if (iv === 9 || iv === 8) b += 0.03; // down a third: I→vi
    else if (iv === 7) b += 0.01;             // up a fifth
    // the authentic cadence itself, over and above the root motion
    if (next.degree === 'I' || next.degree === 'i') {
      if (prev.degree === 'V' || prev.degree === 'V7') b += 0.05;
    }
    return b;
  }

  /* ── which bars are genuinely arguable ──────────────────────────────────
   *
   * ⚠️ The first version of this reused chord-finder's thresholds (0.04 and
   * 0.12) because the segments share a shape. They do not share a SCALE: over
   * there a margin is the gap between two chroma cosines, here it is the gap
   * between two fit scores with the simplicity priors already added. Borrowed,
   * they dashed 40% of every chart — and a mark carried by four bars in ten
   * stops distinguishing anything.
   *
   * So the question is asked exactly instead of thresholded: WHO decided this
   * bar? Each candidate carries a small prior that breaks ties towards the
   * plainer chord. If the winning margin is no bigger than the prior gap
   * between the top two, then the melody did not choose — the prior did, and
   * the other chord would sit just as well. That is precisely what the dashed
   * border claims, it needs no number to tune, and it moves correctly if the
   * priors are ever changed.
   *
   * `high` still needs a number, and 0.12 keeps its meaning here: measured
   * over 20 bars of four fixtures, the margins are bimodal — ties and near
   * ties below 0.08, clear wins from 0.18 up — so anything in between is
   * honestly "medium".
   */
  function confidenceOf(margin, best, second) {
    if (!second) return margin >= 0.12 ? 'high' : 'medium';
    /* And the runner-up has to be a DIFFERENT CHORD, not the same one wearing
     * a seventh. The dashed border tells a player "you may be playing the
     * wrong chord here"; G against G7 is not that — it is the same shape
     * family and the same hand, and warning about it spends the reader's
     * attention on a distinction they cannot act on. */
    if (second.pc === best.pc) return margin >= 0.12 ? 'high' : 'medium';
    var priorGap = Math.abs((best.prior || 0) - (second.prior || 0));
    if (margin <= priorGap + 1e-9) return 'low';
    return margin >= 0.12 ? 'high' : 'medium';
  }

  function candidatesFor(tonic, mode) {
    var table = mode === 'minor' ? MINOR_DEGREES : MAJOR_DEGREES;
    var out = [];
    for (var i = 0; i < table.length; i++) {
      out.push({
        pc: (tonic + table[i].pc) % 12,
        quality: table[i].quality,
        degree: table[i].degree,
        prior: table[i].prior
      });
    }
    return out;
  }

  function scalePcsFor(tonic, mode) {
    var s = mode === 'minor' ? MINOR_SCALE : MAJOR_SCALE;
    var out = [];
    for (var i = 0; i < s.length; i++) out.push((tonic + s[i]) % 12);
    // The raised seventh of the minor is part of the key in practice: it is
    // what makes V major, and V major is in the candidate list.
    if (mode === 'minor') out.push((tonic + 11) % 12);
    return out;
  }

  /**
   * Chords for a melody.
   *
   * @param {Array}  notes  [{from,to,midi}], time in seconds
   * @param {object} opts   { bpm, windowSec, beatsPerChord, key, mode, keyFn }
   * @returns {object|null} { key, mode, keyConfidence, windowSec, segments }
   */
  function harmonize(notes, opts) {
    opts = opts || {};
    if (!notes || notes.length < 2) return null;
    notes = notes.slice().sort(function (a, b) { return a.from - b.from; });

    var k = keyOf(notes, opts);
    if (!k) return null;

    var cands = candidatesFor(k.tonic, k.mode);
    var scalePcs = scalePcsFor(k.tonic, k.mode);
    var changeCost = typeof opts.changeCost === 'number' ? opts.changeCost : CHANGE_COST;
    var wins = windowsFor(notes, opts);
    if (!wins.length) return null;

    // Score every window. A window with no melody in it carries no evidence
    // and inherits whatever the path decides — silence is not a chord change.
    var obs = [], live = [];
    for (var w = 0; w < wins.length; w++) {
      var s = scoreWindow(wins[w], notes, cands, scalePcs);
      obs.push(s);
      if (s) live.push(w);
    }
    if (!live.length) return null;

    // Home is worth something at the edges of the phrase.
    var HOME = { first: 0.08, last: 0.12, half: 0.05 };
    var firstLive = live[0], lastLive = live[live.length - 1];
    for (var c = 0; c < cands.length; c++) {
      var isHome = (cands[c].degree === 'I' || cands[c].degree === 'i');
      var isDom = (cands[c].degree === 'V' || cands[c].degree === 'V7');
      if (obs[firstLive] && isHome) obs[firstLive][c] += HOME.first;
      if (obs[lastLive]) {
        if (isHome) obs[lastLive][c] += HOME.last;
        else if (isDom) obs[lastLive][c] += HOME.half;
      }
    }

    // Viterbi over the windows that have evidence.
    var n = live.length, m = cands.length;
    var dp = [], bp = [];
    var row = new Float64Array(m);
    for (c = 0; c < m; c++) row[c] = obs[live[0]][c];
    dp.push(row);
    for (var i = 1; i < n; i++) {
      var prevRow = dp[i - 1];
      var cur = new Float64Array(m);
      var back = new Int32Array(m);
      for (c = 0; c < m; c++) {
        var best = -Infinity, bi = 0;
        for (var p = 0; p < m; p++) {
          var v = prevRow[p] + motionBonus(cands[p], cands[c], changeCost);
          if (v > best) { best = v; bi = p; }
        }
        cur[c] = best + obs[live[i]][c];
        back[c] = bi;
      }
      dp.push(cur);
      bp.push(back);
    }
    var path = new Int32Array(n);
    var bestEnd = 0;
    for (c = 1; c < m; c++) if (dp[n - 1][c] > dp[n - 1][bestEnd]) bestEnd = c;
    path[n - 1] = bestEnd;
    for (i = n - 2; i >= 0; i--) path[i] = bp[i][path[i + 1]];

    /* Per-window confidence, on chord-finder's scale and for the same reason:
     * a bar the melody barely supports must not be drawn as certain as one it
     * spells out. Margin is against the best OTHER candidate in that window,
     * taken from the observation alone — the path bonus is about the phrase,
     * not about how well this bar is evidenced. */
    var segs = [];
    for (i = 0; i < n; i++) {
      var wi = live[i], ci = path[i], o = obs[wi];
      var second = -Infinity, si = -1;
      for (c = 0; c < m; c++) if (c !== ci && o[c] > second) { second = o[c]; si = c; }
      var margin = Math.max(0, o[ci] - second);
      var cand = cands[ci];
      segs.push({
        from: wins[wi].from,
        to: wins[wi].to,
        root: cand.pc,
        quality: cand.quality,
        sym: SYMS[cand.quality],
        degree: cand.degree,
        margin: margin,
        confidence: confidenceOf(margin, cand, si >= 0 ? cands[si] : null)
      });
    }

    // Merge neighbours that landed on the same chord, so the chart shows a
    // chord held for two bars as one chord held for two bars.
    var merged = [];
    for (i = 0; i < segs.length; i++) {
      var last = merged[merged.length - 1];
      if (last && last.root === segs[i].root && last.quality === segs[i].quality &&
          Math.abs(last.to - segs[i].from) < 1e-6) {
        last.to = segs[i].to;
        last.margin = Math.min(last.margin, segs[i].margin);
        last.confidence = last.margin >= 0.12 ? 'high' : (last.margin >= 0.04 ? 'medium' : 'low');
        continue;
      }
      merged.push(Object.assign({}, segs[i]));
    }

    return {
      key: k.tonic,
      mode: k.mode,
      keyConfidence: k.confidence,
      keyGiven: !!k.given,
      windowSec: wins.length ? (wins[0].to - wins[0].from) : null,
      notes: notes.length,
      segments: merged
    };
  }

  /* ── is this one voice at a time? ───────────────────────────────────────
   *
   * The question /chord-finder has to answer before it prints anything, and
   * the one it was not asking.
   *
   * ⚠️⚠️ NOT FROM THE CHROMAGRAM. The first version of this function asked how
   * concentrated each chroma frame was, on the reasoning that a triad spreads
   * its energy over three pitch classes and a sung note over one. Measured on
   * nine kinds of material, that reasoning is wrong in the one case that
   * decides everything — a SONG, where the vocal sits on top of the chords:
   *
   *   material                 single-note fit minus triad fit
   *   sung melody alone                 +0.143
   *   hummed melody alone               +0.152
   *   solo guitar melody                +0.224
   *   piano chords                      −0.166
   *   guitar strum                      −0.155
   *   SONG: melody over chords          +0.282   ← more "monophonic" than a voice
   *
   * The vocal dominates the chroma whether or not a band is playing under it,
   * so no shape of the chroma can separate the two. A metric that looks right
   * on two fixtures and inverts on the third is worse than none: it would have
   * called every song with a singer in it "one voice".
   *
   * What DOES separate them is whether a single PERIOD explains the waveform,
   * which is what YIN's confidence measures and what polyphony destroys — two
   * fundamentals have no common period to find. Same bench, median confidence
   * over the frames:
   *
   *   sung 0.997 · hummed 0.998 · whistle 1.000 · solo guitar 1.000
   *   song 0.797 · song+drums 0.791 · strum 0.256 · piano chords 0.229
   *   speech 0.192
   *
   * The gap is 0.200 wide and survives sampling the file instead of reading
   * all of it (8 excerpts of 1 s: voice ≥ 0.997, anything else ≤ 0.815), which
   * is what keeps the test affordable on a five-minute song. 0.92 sits inside
   * that gap, biased so that a wrong answer means "read it as harmony", i.e.
   * exactly what the tool did before.
   *
   * Takes pitch frames, not audio, so this module stays free of any decoder.
   * Returns { monophonic, medianConf, frames } — the evidence, not just the
   * verdict, so a caller can report it.
   */
  function monophonyOfPitch(frames, opts) {
    opts = opts || {};
    var minMedianConf = opts.minMedianConf === undefined ? 0.92 : opts.minMedianConf;
    var conf = [];
    for (var i = 0; i < frames.length; i++) {
      var c = pick(frames[i], ['confidence', 'clarity', 'conf']);
      conf.push(c === undefined || c === null ? 0 : c);
    }
    if (conf.length < 20) return { monophonic: false, medianConf: null, frames: conf.length };
    var med = median(conf);
    return { monophonic: med >= minMedianConf, medianConf: med, frames: conf.length };
  }

  /* ── where does the tune LIE, and does it lie where you can sing? ───────
   *
   * /vocal-range measures a singer's lowest and highest note and has done it
   * for 8 858 people in 90 days; /chord-finder can transpose. Neither knew
   * about the other, so «транспонируйте под свой голос» — printed on the page
   * since the tool shipped — was advice rather than something the page could
   * do. These two functions are the join.
   *
   * The tune's range comes from PITCH FRAMES over the mix, not from the chord
   * chart: chords are not a melody. Measured against a mix whose melody the
   * test wrote, the tenth and ninetieth percentiles of the confident frames
   * land within 0.2 of a semitone of the truth while the melody is audible —
   * and when it is not, the SHARE of confident frames collapses (0.14 against
   * 0.49–0.75), which is what `confidentShare` is for. Below that share the
   * honest answer is "we cannot hear the tune in this", not a number.
   */
  function rangeOfPitch(frames, opts) {
    opts = opts || {};
    var minConf = opts.minConf === undefined ? 0.8 : opts.minConf;
    var lowPct = opts.lowPct === undefined ? 0.10 : opts.lowPct;
    var highPct = opts.highPct === undefined ? 0.90 : opts.highPct;
    var m = [];
    for (var i = 0; i < frames.length; i++) {
      var f = frames[i];
      var hz = pick(f, ['freqHz', 'hz', 'freq']);
      var c = pick(f, ['confidence', 'clarity', 'conf']);
      if (!(hz > 0)) continue;
      if (c !== undefined && c !== null && c < minConf) continue;
      m.push(hzToMidi(hz));
    }
    var share = frames.length ? m.length / frames.length : 0;
    if (m.length < 30) return { lo: null, hi: null, confidentShare: share, notes: m.length };
    m.sort(function (a, b) { return a - b; });
    var at = function (q) { return m[Math.min(m.length - 1, Math.max(0, Math.round(q * (m.length - 1))))]; };
    return {
      lo: Math.round(at(lowPct)), hi: Math.round(at(highPct)),
      confidentShare: share, notes: m.length
    };
  }

  /**
   * How far to move a tune so a given voice can sing it.
   *
   * The measured range is an absolute maximum — the notes somebody could just
   * reach on the day they were tested — so the target window sits INSIDE it:
   * a semitone off the bottom and two off the top, because the top is where
   * singing at the edge stops being singing.
   *
   * Among the shifts that fit, the smallest wins. A tune that already fits
   * must come back 0 rather than be moved to the middle for tidiness: the key
   * it is in is part of the song, and moving it without need is a change
   * nobody asked for.
   */
  function fitToRange(mel, voice, opts) {
    opts = opts || {};
    if (!mel || mel.lo === null || mel.hi === null) return null;
    if (!voice || voice.lo === null || voice.hi === null) return null;
    var headroom = opts.headroom === undefined ? 2 : opts.headroom;
    var footroom = opts.footroom === undefined ? 1 : opts.footroom;
    var lo = voice.lo + footroom, hi = voice.hi - headroom;
    if (hi < lo) { lo = voice.lo; hi = voice.hi; }
    var melW = mel.hi - mel.lo, winW = hi - lo;

    if (melW > winW) {
      // Wider than the singer: no shift fits, so centre it and say so. The
      // alternative — refusing — leaves them with nothing, and the centred
      // key is genuinely the least bad.
      var mid = Math.round((lo + hi) / 2 - (mel.lo + mel.hi) / 2);
      return { semitones: mid, fits: false, melodyWidth: melW, windowWidth: winW };
    }
    var best = null;
    for (var n = -12; n <= 12; n++) {
      if (mel.lo + n < lo || mel.hi + n > hi) continue;
      if (best === null || Math.abs(n) < Math.abs(best)) best = n;
    }
    if (best === null) {
      var mid2 = Math.round((lo + hi) / 2 - (mel.lo + mel.hi) / 2);
      return { semitones: mid2, fits: false, melodyWidth: melW, windowWidth: winW };
    }
    return { semitones: best, fits: true, melodyWidth: melW, windowWidth: winW };
  }

  return {
    MAJOR_DEGREES: MAJOR_DEGREES,
    MINOR_DEGREES: MINOR_DEGREES,
    IVALS: IVALS,
    SYMS: SYMS,
    toneFit: toneFit,
    notesFromPitch: notesFromPitch,
    chromaFromNotes: chromaFromNotes,
    candidatesFor: candidatesFor,
    scalePcsFor: scalePcsFor,
    windowsFor: windowsFor,
    scoreWindow: scoreWindow,
    motionBonus: motionBonus,
    harmonize: harmonize,
    monophonyOfPitch: monophonyOfPitch,
    rangeOfPitch: rangeOfPitch,
    fitToRange: fitToRange
  };
});
